import { jsx } from 'panel';
import kebabCase from 'lodash/kebabCase';
import { QueryBlockEl } from 'common/widgets/query-block';
import { serializeAttributes } from 'common/util';
import { SelectEl } from 'common/components/select';
import { EntryPart, SelectorTypeDefinitions } from 'common/widgets/query-entry-section/types';
import { isEnhancedListFilter } from 'common/widgets/property-filter-menu/utils';
import { preventTracking } from 'common/util/dom';
import { PropertyType, ResourceType } from 'common/report/constants';
import { hasNewQBUI, qbStyleAttrs } from 'common/widgets/query-builder/util';
import { TypecastPillAction } from 'common/widgets/query-block/properties-menu/types';
import { BaseEntryRowEl } from '../base/template';
import { FilterValueTemplate, getFilterOperatorsSections, getFilterOperatorSymbol, getFilterOperatorTooltip, getListQuantifierSections, getListQuantifierTooltip } from './utils'; // defines which filter types do not render an operator selector
// this is a temporal check until we can bring all of the new filter
// UI to all filter types

const NO_OPERATOR_TYPES = new Set([PropertyType.Object, PropertyType.NonProperty]);

function createDefaultSelectorRenderer(_ref) {
  let {
    section,
    index
  } = _ref;
  const {
    activeEntry,
    entryPart
  } = section.state;
  const hasProperty = this.filter.hasSelectedProperty();
  const queryBlockConfig = section.getQueryBlockConfig(SelectorTypeDefinitions.Filter);
  const isCohortFilter = this.filter.resourceType === ResourceType.Cohort;
  return () => jsx(null, null, jsx(QueryBlockEl, {
    key: 'entry-selector',
    class: {
      'is-placeholder': !hasProperty
    },
    attrs: serializeAttributes(Object.assign(Object.assign({}, queryBlockConfig), {
      elref: "entrySelector",
      'allow-typecast-pill-action': TypecastPillAction.TriggerTutorial,
      'disable-value-selection': true,
      open: !hasProperty && activeEntry === index && entryPart === EntryPart.Value,
      'data-group-id': section.attr("data-group-id"),
      'single-data-group': section.attr("data-group-id"),
      'inline-cohorts-override': section.attr("inline-cohorts-override"),
      'project-has-cohorts': section.attr("project-has-cohorts"),
      'project-id': section.attr("project-id"),
      readonly: section.attr("readonly"),
      selected: hasProperty ? this.filter.getAttributes() : null,
      'show-special-dataset-property': section.attr("show-special-dataset-property"),
      'hide-special-date-property-unit': section.attr("hide-special-date-property-unit"),
      theme: qbStyleAttrs().selectorTheme,
      'track-name': section.attr("track-name"),
      'selector-type': "button",
      'selector-icon': hasNewQBUI() ? "" : null,
      'user-id': section.attr("user-id"),
      disabled: section.attr("disabled"),
      'workspace-id': section.attr("workspace-id")
    })),
    on: {
      close: () => section.helpers.setActiveEntry("close", index, EntryPart.Value),
      open: () => section.helpers.setActiveEntry("open", index, EntryPart.Value),
      remove: () => section.helpers.onEventActionClick(section.helpers.Action.Remove, index),
      select: ev => this.handlePropertyItemSelected(ev),
      track: preventTracking
    }
  }), isEnhancedListFilter(this.filter) && jsx(SelectEl, {
    attrs: {
      elref: "listFilterQuantifier",
      'menu-width': "200",
      theme: "select",
      tooltip: getListQuantifierTooltip(this.filter.listQuantifier),
      readonly: section.attr("readonly")
    },
    props: {
      sections: getListQuantifierSections(this.filter)
    },
    on: {
      change: _ref2 => {
        let {
          detail
        } = _ref2;
        return this.setFilterAttribute("listQuantifier", detail.item.value);
      }
    }
  }), this.filter.hasSelectedProperty() && !isCohortFilter && !NO_OPERATOR_TYPES.has(this.filter.propertyType) && jsx(SelectEl, {
    attrs: {
      elref: "filterOperator",
      'disable-search': true,
      'menu-width': "200",
      'menu-height': "400",
      readonly: section.attr("readonly"),
      'selected-option': getFilterOperatorSymbol(this.filter),
      theme: "select",
      tooltip: getFilterOperatorTooltip(this.filter)
    },
    class: {
      ["for-".concat(this.filter.propertyType)]: true,
      ["for-".concat(kebabCase(this.filter.filterOperator), "-operator")]: true
    },
    props: {
      sections: getFilterOperatorsSections(this.filter)
    },
    on: {
      change: ev => this.handleFilterOperatorChange(ev)
    }
  }), !isCohortFilter && FilterValueTemplate[this.filter.propertyType] && FilterValueTemplate[this.filter.propertyType]({
    index: this.index,
    activeEntry: this.section.state.activeEntry,
    entryPart: this.section.state.entryPart,
    filter: this.filter,
    handleFilterValueChange: filterValue => this.handleFilterValueChange(filterValue),
    onSelectMenuChange: (ev, index, entryPart) => this.section.helpers.onSelectMenuChange(ev, index, entryPart),
    setActiveEntry: (state, index, part) => this.section.helpers.setActiveEntry(state, index, part),
    updateEntry: (filter, trackingProps) => this.updateEntry(filter, trackingProps),
    eventNames: this.section.attr("event-names"),
    projectId: this.section.attr("project-id"),
    workspaceId: this.section.attr("workspace-id"),
    readOnlyMode: section.attr("readonly")
  }));
}

export function FilterEntryRowEl(_ref3) {
  let {
    section,
    entry,
    index
  } = _ref3;
  return jsx(BaseEntryRowEl, {
    index: index,
    el: section,
    entry: entry,
    rowRenderer: {
      selectorRenderer: createDefaultSelectorRenderer.call(this, {
        section,
        entry,
        index
      }),
      suffixRenderer: null
    },
    isPlaceholder: this.isPlaceholder()
  });
}